/**
 * רישום אימות דו-שלבי — enrolment. `packages/authn`, 04 §2.2 / §2.3.
 *
 * ── WHAT ARRIVES, AND WHAT DOES NOT ─────────────────────────────────────────────────────────────
 *
 * A secret to enrol, an account label, ten recovery codes shown ONCE, and the policy that made this
 * required. What never arrives, here or anywhere: a password. 04 §2.2 — *"No path may ever produce,
 * transmit or display a password."*
 *
 * ── THE QR IS THE SERVER'S, AND ITS ABSENCE IS WRITTEN OUT ──────────────────────────────────────
 *
 * `provisioningUri` takes `issuer` and `accountName` as ARGUMENTS, deliberately: a hardcoded issuer
 * would be a product name baked into every enrolled device, not merely into the source, and a rename
 * cannot reach an authenticator app that was set up last year. So the URI — and the QR rendered from
 * it — come from the server with the issuer resolved from configuration.
 *
 * Until that arrives this screen shows the SECRET, which is the manual path every authenticator
 * supports, and states plainly that the QR is not available. It does not draw a placeholder pattern
 * that looks like a QR: a code nobody can scan is worse than a stated absence, and this milestone's
 * honesty rule is exactly about the difference.
 *
 * ── ENROLMENT IS UNAVOIDABLE WHERE IT IS REQUIRED ───────────────────────────────────────────────
 *
 * `afterFirstFactor` returns `MFA_ENROLMENT_REQUIRED` for a required-but-unenrolled principal, and
 * that principal is admitted to enrolment AND TO NOTHING ELSE. They are not let in "until they get
 * round to it" and are not silently downgraded to a password-only session — the two failure modes
 * that turn a mandate into a suggestion. For a platform account the requirement is the PRODUCT
 * FLOOR and no tenant policy can lower it, which is why the source of the requirement is named on
 * screen rather than just its existence.
 *
 * ── THE RECOVERY CODES ARE SHOWN ONCE ───────────────────────────────────────────────────────────
 *
 * Only their hashes are stored, so there is no later screen that can show them again, and saying so
 * at the moment they are displayed is the only time it helps.
 *
 * ── AND ENROLMENT IS ITSELF STEP-UP GUARDED ────────────────────────────────────────────────────
 *
 * `auth.mfa.enrol` can answer 403 with `need`, and that answer is NOT a denial: the gate allowed the
 * request, and the handler refused because the session is stale or a factor exists and has not been
 * presented. *Establishing a new second factor with a stale session is how an unattended browser
 * becomes an enrolled device.*
 *
 * So this screen distinguishes the three needs rather than showing one "אין הרשאה". `REAUTH` sends
 * the person to sign in; `MFA` sends them to the CHALLENGE, because they already hold a working
 * authenticator and enrolling again would create the second device the guard exists against; and
 * `ENROLMENT` is the one that ends here. `<ActionFailureNotice>` does that routing from `stepUpText`,
 * so this screen never decides which door it is.
 */

import { useState } from 'react';
import type { TextKey } from '../i18n/catalogue';
import { useLocale } from '../i18n/LocaleProvider';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { QrCode } from '../components/QrCode';
import { StateBlock } from '../components/StateBlock';
import { RECOVERY_CODE_COUNT, TOTP_DIGITS } from '../api/contract';
import { mfaPolicyHe } from '../api/adminVocabulary';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { readProvisioning } from '../api/provisioning';
import type { ProvisioningDetails } from '../api/provisioning';
import { fixtureMfaEnrolment, fixturesEnabled } from '../api/fixtures';
import { routes } from '../routes';


const REQUIRED_BY_KEY: Readonly<
  Record<'product_floor' | 'tenant_policy' | 'not_required', TextKey>
> = {
  // No tenant setting can lower this one, and the wording says so rather than implying a choice.
  product_floor: 'enrol.mandatorySystem',
  tenant_policy: 'enrol.mandatoryOrg',
  not_required: 'enrol.optional',
};

/** Base32 in groups of four, so it can be read aloud and typed without losing the place. */
export function groupSecret(secret: string): string {
  return (secret.match(/.{1,4}/gu) ?? []).join(' ');
}

/** Two writes, and the second one only exists because the first produced a challenge reference. */
type Outcome =
  | { readonly kind: 'awaitingConfirmation' }
  | { readonly kind: 'enrolled' }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

/**
 * CM-024. **THE SECRET COMES FROM THE SERVER.**
 *
 * This screen used to display `fixtureMfaEnrolment.secretBase32` — a fixed, published Base32 string
 * — as the manual key, to every user, while confirming against the server's real challenge. Two
 * defects in one line: a code from that secret could never match, so enrolment could not complete;
 * and anyone who did scan it enrolled a secret this repository publishes.
 *
 * The server already returned the provisioning URI. What was missing was the declaration of it and a
 * screen that read it. Nothing on the server changed.
 *
 * ── AND THE ORDER CHANGED WITH IT ───────────────────────────────────────────────────────────────
 *
 * The old screen showed recovery codes as step 2, BEFORE confirmation. They do not exist then:
 * `confirmMfaEnrolment` mints them and stores only their hashes, and it does that only once a code
 * has proved the secret reached the device. So the codes appear after confirmation, which is also
 * the only order in which the sentence *"these are shown once"* is true.
 */
export interface MfaEnrolmentScreenProps {
  /**
   * The ENROL challenge issued at sign-in, from the ADDRESS -- `null` when enrolling from inside a
   * bound session, which is the other accepted proof.
   *
   * NOT the same value as the `challengeRef` state below. That one is minted by `auth.mfa.enrol`
   * and names the enrolment row that `confirm` completes; this one proves the FIRST factor was
   * already presented. Conflating them would send the confirmation's ref as the proof of a password
   * nobody had typed, and the two are both opaque strings, so nothing would have complained.
   *
   * Read by the ROUTE and passed in, exactly as `MfaChallengeScreen` takes its own, so this screen
   * stays renderable without a router.
   */
  readonly signInChallengeRef: string | null;
}

export function MfaEnrolmentScreen({
  signInChallengeRef,
}: MfaEnrolmentScreenProps): JSX.Element {
  const { t } = useLocale();

  const [code, setCode] = useState('');
  const [challengeRef, setChallengeRef] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  /** From the server's provisioning URI. Never persisted, never logged, never put in an address. */
  const [enrolment, setEnrolment] = useState<ProvisioningDetails | null>(null);
  /** Minted by the CONFIRMATION, so `null` until the code proved the secret arrived. */
  const [recoveryCodes, setRecoveryCodes] = useState<readonly string[] | null>(null);

  const offer = fixturesEnabled() ? fixtureMfaEnrolment : null;

  /**
   * Begins enrolment. The response carries the provisioning URI — which CONTAINS THE SHARED SECRET —
   * and this screen never logs it, never stores it and never puts it in an address; it is displayed
   * once, as the manual key, in the block below.
   */
  async function begin(): Promise<void> {
    setSending(true);
    setOutcome(null);
    try {
      /*
       * ── THE PROOF OF THE FIRST FACTOR TRAVELS HERE, AND USED NOT TO. **2026-10-15.** ────────
       *
       * This sent `{}`. The route accepts a bound session OR the ENROL challenge ref from
       * sign-in, and a PLATFORM OWNER arriving here holds NEITHER a cookie nor any other proof --
       * the sign-in arm that sent them here sets no session on purpose. So the one call that
       * begins enrolment presented nothing the route accepts and was refused, and the first owner
       * of a fresh estate could not enrol the factor the platform floor REQUIRES them to hold.
       *
       * `App.tsx` had been putting the ref in the address all along; the enrolment route rendered
       * this screen directly and dropped it, while the challenge route beside it reads the same
       * parameter and passes it down. The address was right, the contract type forbade the field,
       * and nothing read it.
       *
       * Omitted when null, so the session-bound arm still sends no field at all.
       */
      const started = await callOperation('auth.mfa.enrol', {
        body: signInChallengeRef === null ? {} : { challengeRef: signInChallengeRef },
      });
      const details = readProvisioning(started.provisioning);
      if (details === null) {
        // A provisioning URI this build cannot read is not a key to show anyway. Displaying the raw
        // string would hand somebody a value to type into an authenticator that will not accept it.
        // `unreachable` is the honest classification: the server answered, and what it answered
        // cannot be used. There is no arm for "we got something we could not read", and inventing
        // one would put a sixth answer in a taxonomy that exists to have five.
        setOutcome({ kind: 'failed', failure: { kind: 'unreachable' } });
        return;
      }
      setChallengeRef(started.challengeRef);
      setEnrolment(details);
      setOutcome({ kind: 'awaitingConfirmation' });
    } catch (err: unknown) {
      // A 403 here is usually a STEP-UP, not a denial. `<ActionFailureNotice>` names which challenge.
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setSending(false);
    }
  }

  function submit(e: FormEvent): void {
    e.preventDefault();
    setSending(true);
    setOutcome(null);
    void (async () => {
      try {
        // The verification itself is `verifyTotp` on the server, with the replay defence that makes a
        // code single-use within its window. Nothing here decides whether a code is correct.
        //
        // Confirming with a code PROVES the secret reached the device. Enrolling without it would
        // leave a principal holding a factor they cannot present — a lockout the product created.
        // The challenge is the one `begin()` received. There is no arm for "confirm without having
        // begun": the route reads the ref from the body and would answer for whatever row it names.
        if (challengeRef === null) return;
        const confirmed = await callOperation('auth.mfa.enrolment.confirm', {
          body: { challengeRef, code },
        });
        // Shown ONCE. The response is the only time these exist outside the user's hands — the
        // server stores hashes — so they are held in component state and nowhere else.
        setRecoveryCodes(confirmed.recoveryCodes.split('\n').filter((line) => line !== ''));
        setOutcome({ kind: 'enrolled' });
      } catch (err: unknown) {
        setOutcome({ kind: 'failed', failure: classifyActionError(err) });
      } finally {
        setSending(false);
      }
    })();
  }

  return (
    <AppShell screenId="mfa-enrolment"
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated
      titleHe={t('personal.mfaEnrol')}
    >
      {offer === null ? null : (
        // The POLICY — which office requires MFA, and why — is still development data, and is
        // labelled as such by the shell's fixture ribbon. It carries no credential.
        <section className="notice" data-tone="info">
          <h2 className="notice__title">{mfaPolicyHe(offer.policy)}</h2>
          <p className="notice__body">{t(REQUIRED_BY_KEY[offer.requiredBy])}</p>
        </section>
      )}

      {/*
        ── THE STEPS NOW NAME THE ONE THAT WAS MISSING. **2026-10-25.** ──────────────────────────

        Installing the app was step zero and the screen never mentioned it, so somebody arriving
        here with no authenticator installed was looking at a key and a code field with nothing
        telling them what to put the key INTO. The Owner asked for it in those words: *"הוראות
        קצרות: התקנה ← הוספת חשבון באמצעות QR או מפתח ידני ← הזנת קוד לאישור"*.

        `data-state` follows what has actually happened, so the list is a progress indicator rather
        than a decoration: the current step is also marked `aria-current`, and the numbers and the
        border carry it for anybody who cannot see the colour.
      */}
      <ol className="steps">
        <li
          className="steps__item"
          data-state={enrolment === null ? 'current' : 'done'}
          {...(enrolment === null ? { 'aria-current': 'step' as const } : {})}
        >
          <span className="steps__index" dir="ltr">
            1
          </span>
          <span className="steps__label">{t('enrol.step1')}</span>
        </li>
        <li
          className="steps__item"
          data-state={enrolment === null ? 'pending' : recoveryCodes === null ? 'current' : 'done'}
          {...(enrolment !== null && recoveryCodes === null ? { 'aria-current': 'step' as const } : {})}
        >
          <span className="steps__index" dir="ltr">
            2
          </span>
          <span className="steps__label">{t('enrol.step2')}</span>
        </li>
        <li
          className="steps__item"
          data-state={recoveryCodes === null ? 'pending' : 'done'}
          {...(recoveryCodes !== null ? { 'aria-current': 'step' as const } : {})}
        >
          <span className="steps__index" dir="ltr">
            3
          </span>
          <span className="steps__label">{t('enrol.step3')}</span>
        </li>
      </ol>

      {/*
        ── WHICH APP, AND WHERE TO GET IT. **Owner instruction, 2026-10-25.** ────────────────────

        *"קישורי הורדה ברורים ליישומון אימות ב־Android וב־iPhone, לפני תחילת הרישום."* Before, not
        after: the registration window starts when the person presses the button below, so sending
        them to an app store afterwards is how the window runs out.

        THE LINKS GO TO THE STORES' OWN SEARCH PAGES, not to one vendor's product page. Naming a
        single app would be GL-POINT recommending a third party we have not reviewed and cannot
        vouch for, and any TOTP app works — which is the actual fact worth telling somebody.

        `rel="noreferrer"` with `target="_blank"`: a new tab, because this one is holding a
        registration in progress and navigating away from it loses the key.
      */}
      {enrolment !== null ? null : (
        <section className="enrolment" aria-labelledby="mfa-install-title">
          <h2 className="enrolment__title" id="mfa-install-title">
            {t('enrol.step1Numbered')}
          </h2>
          <p>
            {t('enrol.appsHint')}
          </p>
          <div className="actions-row">
            <a
              className="btn btn--nav"
              href="https://play.google.com/store/search?q=authenticator&c=apps"
              target="_blank"
              rel="noreferrer"
            >
              {t('enrol.downloadAndroid')}
            </a>
            <a
              className="btn btn--nav"
              href="https://apps.apple.com/search?term=authenticator"
              target="_blank"
              rel="noreferrer"
            >
              {t('enrol.downloadIphone')}
            </a>
          </div>
          <p className="field__hint">
            {t('enrol.installFirst')}
          </p>
        </section>
      )}

      <div className="actions-row">
        <button type="button" className="btn" onClick={() => void begin()} disabled={sending}>
          {sending ? t('common.sending') : t('enrol.begin')}
        </button>
        <a className="btn" href={routes.personalSecurity}>
          {t('personal.title')}
        </a>
      </div>

      <section className="enrolment">
        <h2 className="enrolment__title">{t('enrol.sectionAdd')}</h2>

        {enrolment === null ? (
          // Before `begin()` there is no secret, and there must be nothing here that looks like one.
          // This is where the fixture key used to sit.
          <div className="enrolment__qr enrolment__qr--absent" role="note">
            <p className="enrolment__qr-title">{t('enrol.notStarted')}</p>
            <p className="enrolment__qr-body">
              {t('enrol.keyOnlyOnce')}
            </p>
          </div>
        ) : (
          /*
           * ── THE QR, GENERATED HERE. **Owner approval, 2026-10-25, with its condition.** ───────
           *
           * *"מאשר הוספת רכיב QR ... בתנאי שהיצירה מקומית וללא שליחת המפתח לשירות חיצוני."*
           * `<QrCode>` encodes with a zero-dependency library bundled into this app; the URI never
           * leaves the browser. See that component for how the condition is met.
           *
           * WHAT THE SCREEN USED TO SAY WAS OUT OF DATE, not merely unhelpful. It read *"הקוד נוצר
           * בשרת ... עד שהשרת יחובר"* and keyed off `offer.qrImageDataUri` — a field declared in
           * the web contract that NO SERVER CODE ANYWHERE FILLS, on an `offer` that is development
           * fixtures only. So the branch was unreachable in every build, and the explanation blamed
           * a server connection that had in fact been sending the URI all along. The QR had simply
           * never been implemented.
           *
           * `enrolment.uri` is what the server sent and it CONTAINS the shared secret, which is why
           * it goes straight into the encoder and nowhere else.
           *
           * The manual key stays below, always. Scanning is the convenience; typing is the path
           * that works on a desktop authenticator, a locked-down phone camera, or a device whose
           * camera the person simply does not want to point at their screen.
           */
          <div className="enrolment__qr">
            <QrCode value={enrolment.uri} labelHe={t('enrol.qrCaption')} sizePx={196} />
            <p className="enrolment__qr-body">
              {t('enrol.scanOrManual')}
            </p>
          </div>
        )}

        <dl className="facts">
          <div className="facts__row">
            <dt className="facts__label">{t('enrol.accountName')}</dt>
            <dd className="facts__value" dir="ltr">
              {enrolment?.accountLabel ?? '—'}
            </dd>
          </div>
          <div className="facts__row">
            <dt className="facts__label">{t('enrol.manualKey')}</dt>
            <dd className="facts__value">
              {enrolment === null ? (
                <span className="facts__absent">—</span>
              ) : (
                <code className="secret" dir="ltr">
                  {groupSecret(enrolment.secretBase32)}
                </code>
              )}
            </dd>
          </div>
          <div className="facts__row">
            <dt className="facts__label">{t('enrol.codeKind')}</dt>
            <dd className="facts__value">
              {`${t('enrol.rotatingCodePrefix')} ${String(TOTP_DIGITS)} ${t('enrol.rotatingCodeSuffix')}`}
            </dd>
          </div>
        </dl>
      </section>

      <section className="enrolment">
        <h2 className="enrolment__title">{t('enrol.sectionConfirm')}</h2>
        <form className="form" onSubmit={submit}>
          <label className="field">
            <span className="field__label">{t('enrol.codeFromApp')}</span>
            <input
              className="field__input"
              dir="ltr"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={TOTP_DIGITS}
              value={code}
              onChange={(e) => setCode(e.currentTarget.value)}
              required
            />
          </label>
          <div className="actions-row">
            <button type="submit" className="btn btn--primary" disabled={sending}>
              {sending ? t('common.sending') : t('enrol.complete')}
            </button>
            {/*
              ── START AGAIN, AS A CONTROL ON THE SCREEN. **Owner instruction, 2026-10-25.** ─────

              *"הודעת כשל מועילה וכפתור להתחלה מחדש."* Pressing it calls `begin()` again, which
              issues a NEW challenge and a NEW secret -- and, since this round, REVOKES the earlier
              one on the server (`revokeOtherPendingEnrolments`). So it is both the recovery from a
              closed registration window and the way to retire a key that has been seen.

              The person must re-add the account in their authenticator afterwards, because the
              secret genuinely changed. The hint under the button says so rather than leaving them
              to discover it when the next code is refused.
            */}
            <button
              type="button"
              className="btn btn--nav"
              onClick={() => void begin()}
              disabled={sending}
            >
              {t('enrol.restart')}
            </button>
          </div>
          <p className="field__hint">
            {t('enrol.restartNote')}
          </p>
        </form>
      </section>

      {outcome === null ? null : outcome.kind === 'awaitingConfirmation' ? (
        <StateBlock
          titleHe={t('enrol.startedTitle')}
          bodyHe={t('enrol.startedBody')}
          tone="unavailable"
          nextStep={<p>{t('enrol.addThenConfirm')}</p>}
        />
      ) : outcome.kind === 'enrolled' ? (
        /*
         * ── THE RECOVERY CODES. **A DEFECT, FOUND 2026-10-25 WHILE CHECKING FOR THIS.** ─────────
         *
         * The Owner asked for *"הצגת קודי שחזור והסבר לשמירתם לאחר השלמת הרישום"*, and the honest
         * answer to "is it there already" was no. `recoveryCodes` was put in state by `submit()`
         * and **rendered by nothing** -- two references in the whole file, the `useState` and the
         * setter. Meanwhile this very block told the person that the codes *"שהוצגו"* -- that were
         * shown -- could not be shown again. They were never shown.
         *
         * So the server minted ten codes, handed them over once, the screen dropped them, and then
         * said they were gone. That is the path that saves somebody who loses their phone, and it
         * was silently empty.
         *
         * They are rendered HERE and nowhere else, from component state, which ends when the screen
         * does. `spellCheck={false}` and `dir="ltr"` because they are not words.
         */
        <section className="enrolment" aria-labelledby="mfa-recovery-title">
          <h2 className="enrolment__title" id="mfa-recovery-title">
            {t('enrol.step3Numbered')}
          </h2>
          <StateBlock
            titleHe={t('enrol.doneTitle')}
            bodyHe={t('enrol.doneBody')}
          />
          {recoveryCodes === null || recoveryCodes.length === 0 ? (
            <StateBlock
              titleHe={t('enrol.noRecoveryTitle')}
              bodyHe={t('enrol.noRecoveryBody')}
              tone="error"
              nextStep={<p>{t('enrol.askSupportForCodes')}</p>}
            />
          ) : (
            <>
              <p>
                {t('enrol.recoveryWhat')}
              </p>
              <ul className="recovery-codes" dir="ltr" spellCheck={false}>
                {recoveryCodes.map((rc) => (
                  <li className="recovery-codes__item" key={rc}>
                    <code>{rc}</code>
                  </li>
                ))}
              </ul>
              {/*
                THE SENTENCE THAT HAS TO BE TRUE AT THE MOMENT IT IS READ. Only hashes are stored,
                so there is no later screen that could show these again -- saying so while they are
                still on screen is the only time it helps anybody. SHORTENED, not removed, in the
                2026-10-28 sweep: a one-way store is a fact the reader cannot act without, and the
                moment to say it is while the codes are in front of them.
              */}
              <p className="field__hint">{t('enrol.recoveryOneWay')}</p>
            </>
          )}
          <div className="actions-row">
            <a className="btn btn--nav" href={routes.personalSecurity}>
              {t('enrol.savedThem')}
            </a>
          </div>
        </section>
      ) : (
        <ActionFailureNotice
          failure={outcome.failure}
          /*
           * ── WHAT HAPPENED AND WHAT TO DO, INSTEAD OF A CODE. **Owner instruction, 2026-10-25.**
           *
           * *"קוד שגיאה לבדו אינו מספיק ... כשסיבת הכשל אינה ידועה, אמור זאת במפורש, בלי לנחש."*
           *
           * THE SERVER DELIBERATELY WILL NOT SAY WHICH REFUSAL THIS WAS. `auth.mfa.enrolment.confirm`
           * answers the SAME 401 for an unknown ref, an expired ref and a wrong code, so that it
           * cannot be used to discover which references are real. That is a control worth keeping,
           * and it means this screen genuinely does not know the cause.
           *
           * So it says so, and then names the two things the person can check and the one thing
           * they can do -- which is the Owner's own wording. It does NOT assert that the window
           * expired: that would be the guess the instruction rules out.
           */
          errorGuidance={{
            whatHappenedHe:
              t('enrol.failedBody') +
              t('enrol.failedWhich'),
            whatToDoHe:
              t('enrol.failedNext') +
              t('enrol.failedRestart'),
            action: (
              <button
                type="button"
                className="btn btn--nav"
                onClick={() => void begin()}
                disabled={sending}
              >
                {t('enrol.restart')}
              </button>
            ),
          }}
        />
      )}
    </AppShell>
  );
}
